/**
 * @file LanguageSelection.jsx
 * @author Rishikesh
 * @date 2024-09-18
 * @description Component of master settings to manage languages
 */
import { ContentPasteSearchOutlined } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";

export const LanguageSelection = ({
  isLoading,
  languages,
  newLanguage,
  setNewLanguages,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");

  const isChecked = (language) => {
    return newLanguage?.find((c) => c === language.id) ? true : false;
  };

  const filteredLanguages = languages.filter((item) =>
    item.languagename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (language) => () => {
    setNewLanguages((prev) => {
      const exists = prev.find((c) => c === language);
      if (exists) {
        return prev.filter((c) => c !== language);
      } else {
        return [...prev, language];
      }
    });
  };
  return (
    <>
      <Stack
        py={1}
        px={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" sx={{ mt: 3 }} width={180} height={24} />
        ) : (
          <Typography fontWeight={500} fontSize={20} color="#222222">
            Language Options
          </Typography>
        )}
      </Stack>
      <Box px={2}>
        {isLoading ? (
          <Skeleton variant="rounded" sx={{ my: 1 }} width={380} height={24} />
        ) : (
          <Typography color="textDisabled" fontSize={14}>
            Select the languages which is required for this country
          </Typography>
        )}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 2, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <TextField
            id="outlined-start-adornment"
            placeholder="Search"
            fullWidth
            className="inputMdRounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: "transparent" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContentPasteSearchOutlined />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>
      <List dense sx={{ width: "100%", flex: 1, px: 3, overflowY: "auto" }}>
        {isLoading
          ? Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                sx={{ mt: 1 }}
                width={"100%"}
                height={32}
              />
            ))
          : filteredLanguages?.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    onChange={handleToggle(item.id)}
                    checked={isChecked(item)}
                    sx={{
                      display: isChecked(item) ? "inline-flex" : "none",
                    }}
                  />
                }
                disablePadding
                sx={{
                  bgcolor: isChecked(item) ? "#E4EBF4" : "transparent",
                  "&:hover": {
                    "& .MuiCheckbox-root": {
                      display: "inline-flex",
                    },
                  },
                }}
              >
                <ListItemText
                  sx={{ py: 1, px: 2 }}
                  primary={item.languagename}
                />
              </ListItem>
            ))}
      </List>
    </>
  );
};
